/**
 * ============================================================================
 * USE ADMIN HOOK
 * ============================================================================
 * 
 * Custom hook for admin operations
 */

import { useState, useCallback } from 'react';
import * as adminService from '../services/admin';

export const useAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [settings, setSettings] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);

  /**
   * Get dashboard metrics
   * @param {Object} params - Optional { timeRange: '7d' | '30d' | ... }
   */
  const loadDashboardMetrics = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getDashboardMetrics(params);
      const data = response?.data ?? response;
      setDashboardMetrics(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load dashboard metrics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get analytics
   */
  const loadAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getAnalytics(filters);
      setAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List all transactions (admin)
   */
  const listTransactions = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.listTransactions(filters);
      const data = response?.data ?? response;
      const list = data?.transactions ?? response?.transactions ?? (Array.isArray(data) ? data : []);
      setTransactions(Array.isArray(list) ? list : []);
      return data ?? response;
    } catch (err) {
      setError(err.message || 'Failed to list transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get transaction details (admin)
   */
  const getTransactionDetails = useCallback(async (transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getTransactionDetails(transactionId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get transaction details');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Force cancel transaction
   */
  const forceCancelTransaction = useCallback(async (transactionId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.cancelTransaction(transactionId, { reason });
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to cancel transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List all disputes (admin)
   */
  const listDisputes = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.listDisputes(filters);
      const data = response?.data ?? response;
      const list = data?.disputes ?? (Array.isArray(data) ? data : []);
      setDisputes(Array.isArray(list) ? list : []);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to list disputes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get dispute details (admin)
   */
  const getDisputeDetails = useCallback(async (disputeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getDisputeDetails(disputeId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get dispute details');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Start mediation
   */
  const startMediation = useCallback(async (disputeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.startMediation(disputeId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to start mediation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Resolve dispute
   */
  const resolveDispute = useCallback(async (disputeId, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.resolveDispute(disputeId, data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to resolve dispute');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add dispute note
   */
  const addDisputeNote = useCallback(async (disputeId, note) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.addDisputeNote(disputeId, { note });
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to add dispute note');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List all users (admin)
   */
  const listUsers = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.listUsers(filters);
      const data = response?.data ?? response;
      const list = data?.users ?? (Array.isArray(data) ? data : []);
      setUsers(Array.isArray(list) ? list : []);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to list users');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get user details (admin)
   */
  const getUserDetails = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getUserDetails(userId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get user details');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Suspend user
   */
  const suspendUser = useCallback(async (userId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.suspendUser(userId, { reason });
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to suspend user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reactivate user
   */
  const reactivateUser = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.reactivateUser(userId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to reactivate user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Ban user
   */
  const banUser = useCallback(async (userId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.banUser(userId, { reason });
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to ban user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify user
   */
  const verifyUser = useCallback(async (userId, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.verifyUser(userId, data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to verify user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get financial overview
   */
  const loadFinancialOverview = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getFinancialOverview(filters);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load financial overview');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List withdrawals with optional filters (status, page, limit)
   */
  const listWithdrawals = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.listWithdrawals(filters);
      const data = response?.data ?? response;
      const list = data?.withdrawals ?? (Array.isArray(data) ? data : []);
      setWithdrawals(Array.isArray(list) ? list : []);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to list withdrawals');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List pending withdrawals (convenience: status=pending)
   */
  const listPendingWithdrawals = useCallback(async (filters = {}) => {
    return listWithdrawals({ ...filters, status: 'pending' });
  }, [listWithdrawals]);

  /**
   * Process withdrawal
   */
  const processWithdrawal = useCallback(async (withdrawalId, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.approveWithdrawal(withdrawalId, data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to process withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reject withdrawal
   */
  const rejectWithdrawal = useCallback(async (withdrawalId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.rejectWithdrawal(withdrawalId, { reason });
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to reject withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get system settings
   */
  const loadSettings = useCallback(async (category) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getSettings({ category });
      setSettings(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load settings');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update system setting
   */
  const updateSystemSetting = useCallback(async (key, value) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.updateSystemSetting(key, value);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to update setting');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get audit logs
   */
  const loadAuditLogs = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getAuditLogs(filters);
      setAuditLogs(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load audit logs');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get admin profile
   */
  const loadAdminProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getAdminProfile();
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load admin profile');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get admin activity
   */
  const loadAdminActivity = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getAdminActivity();
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load admin activity');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get system health
   */
  const loadSystemHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getSystemHealth();
      setSystemHealth(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load system health');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear cache
   */
  const clearCache = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.clearCache();
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to clear cache');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Run reconciliation
   */
  const runReconciliation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.runReconciliation();
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to run reconciliation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List all notifications (admin)
   */
  const listAllNotifications = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.listAllNotifications(filters);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to list notifications');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Mark notification as read (admin)
   */
  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.markNotificationAsRead(notificationId);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to mark notification as read');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete notification (admin)
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.deleteNotification(notificationId);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to delete notification');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send notification (admin)
   */
  const sendNotification = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.sendNotification(data);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to send notification');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    dashboardMetrics,
    analytics,
    transactions,
    disputes,
    users,
    withdrawals,
    settings,
    auditLogs,
    systemHealth,
    loading,
    error,
    
    // Actions
    loadDashboardMetrics,
    loadAnalytics,
    listTransactions,
    getTransactionDetails,
    forceCancelTransaction,
    listDisputes,
    getDisputeDetails,
    startMediation,
    resolveDispute,
    addDisputeNote,
    listUsers,
    getUserDetails,
    suspendUser,
    reactivateUser,
    banUser,
    verifyUser,
    loadFinancialOverview,
    listWithdrawals,
    listPendingWithdrawals,
    processWithdrawal,
    rejectWithdrawal,
    loadSettings,
    updateSystemSetting,
    loadAuditLogs,
    loadAdminProfile,
    loadAdminActivity,
    loadSystemHealth,
    clearCache,
    runReconciliation,
    listAllNotifications,
    markNotificationAsRead,
    deleteNotification,
    sendNotification,
    listAllEscrows: adminService.listAllEscrows,
    getEscrowDetails: adminService.getEscrowDetails,
    adminPartialRelease: adminService.adminPartialRelease,
    adminRefundEscrow: adminService.adminRefundEscrow,
    listAllPayments: adminService.listAllPayments,
    getPaymentDetails: adminService.getPaymentDetails,
    retryPayment: adminService.retryPayment,
    refundPayment: adminService.refundPayment,
  };
};

export default useAdmin;
